/**
 * The mini-cart's notices in Finnish, and a split line explained.
 *
 * WooCommerce's interactive cart writes two notices of its own after a change,
 * in English and untranslatable -- they are plain strings in its script, not
 * passed through its translations:
 *
 *   The quantity of "%1$s" was changed to %2$d.
 *   "%s" was removed from your cart.
 *
 * The first also comes when nothing went wrong. On Diakon Säätiö a product
 * bought one per person -- an allergy surcharge, a child's registration --
 * raised to two in the mini-cart is split into two lines of one
 * (diakonsaatio-store Cart\CoupleLines), and the mini-cart, finding the line
 * at one, says it was changed to 1. Where the cart now holds the product on
 * more than one line, the notice says it was added, that each person has
 * their own line, and how many there are now.
 *
 * The notices are rewritten as they appear: WooCommerce sets each one's text
 * once, into a span, and this rewrites the span. The texts come from
 * lib/woocommerce.php (window.diakonStore.cartNotices), in Finnish from the
 * theme's translations.
 */

const NOTICE = '.wc-block-components-notice-banner__content';
const CHANGED = /^The quantity of "(.+)" was changed to (\d+)\.$/;
const REMOVED = /^"(.+)" was removed from your cart\.$/;

/**
 * How many lines of the cart hold a product of this name, in the mini-cart.
 *
 * @param {string} name The product's name.
 * @return {number} Its lines.
 */
function linesOf(name) {
	// By row, each row's first name: a row shows the name in more than one
	// place, and counting those counted each line twice.
	return [
		...document.querySelectorAll(
			'.wc-block-mini-cart__drawer .wc-block-cart-items__row'
		),
	].filter(
		(row) =>
			row
				.querySelector('.wc-block-components-product-name')
				?.textContent.trim() === name
	).length;
}

/**
 * The Finnish for a notice, or null for one this does not know.
 *
 * @param {string} text  The notice as WooCommerce wrote it.
 * @param {Object} texts window.diakonStore.cartNotices.
 * @return {string|null} The notice to show.
 */
function translate(text, texts) {
	const changed = text.match(CHANGED);

	if (changed) {
		const [, name, quantity] = changed;

		const lines = linesOf(name);

		return lines > 1
			? texts.split.replace('%1$s', name).replace('%2$d', lines)
			: texts.changed.replace('%1$s', name).replace('%2$d', quantity);
	}

	const removed = text.match(REMOVED);

	return removed ? texts.removed.replace('%s', removed[1]) : null;
}

/**
 * Rewrites the notices in or under an element: the text WooCommerce put in
 * a notice banner, when it is one of its English notices.
 *
 * @param {Element} root  Where something changed.
 * @param {Object}  texts window.diakonStore.cartNotices.
 */
function rewrite(root, texts) {
	const inside = root.closest(NOTICE);
	const notices = inside ? [inside] : root.querySelectorAll(NOTICE);

	notices.forEach((notice) => {
		// The text sits in a span of its own, beside a dismiss button.
		const holder =
			[...notice.querySelectorAll('span')].find(
				(span) => !span.children.length && span.textContent.trim()
			) || notice;
		const finnish = translate(holder.textContent.trim(), texts);

		if (finnish) {
			holder.textContent = finnish;
		}
	});
}

export function initCartNotices() {
	const texts = window.diakonStore?.cartNotices;

	if (!texts) {
		return;
	}

	// The cart's lines and the notice change in the same update; looked at
	// once the update is done, so the split line's lines are all there.
	let queued = new Set();

	const observer = new window.MutationObserver((mutations) => {
		mutations.forEach((mutation) => {
			const target =
				mutation.target.nodeType === 1
					? mutation.target
					: mutation.target.parentElement;

			if (target) {
				queued.add(target);
			}
		});

		window.requestAnimationFrame(() => {
			const elements = queued;
			queued = new Set();
			elements.forEach((element) => rewrite(element, texts));
		});
	});

	observer.observe(document.body, {
		childList: true,
		characterData: true,
		subtree: true,
	});
}
